/** CLIENTS — users joined to their portfolios. */
import { useCallback, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { GET_CLIENTS, GET_PROSPECTS, KYC_ONBOARDING } from '../queries';
import { portfolioScope, portfolioScopeLabel } from '../../lib/portfolio';
import { looksLikePhone, phoneLabel } from '../../lib/format';
import { useBook } from './book';
import { useClientScope } from './firm';
import { useProfile } from './account';
import { EMPTY, sum, byDesc } from './_shared';

/**
 * How a client is named on screen.
 *
 * An account that signed up by phone has the NUMBER stored as its name
 * ("+919502042793") and no email at all — more than half of them here — so a
 * screen that prints `name` shows a bare number where a person should be, and
 * the deep dive's picker reads as an id. A real name wins; a name that is only
 * a phone number is shown as a phone, spaced so it reads as a contact rather
 * than as someone called 919502042793; an account with neither gets "Client
 * <id>", which at least says what it is. `named` is false in those last two
 * cases, for a screen that wants to ask for the real name.
 */
export function clientName(u) {
  const name = String(u?.name ?? '').trim();
  if (name && !looksLikePhone(name)) return { label: name, named: true };
  const email = String(u?.email ?? '').trim();
  if (email) return { label: email, named: false };
  const phone = phoneLabel(u?.phoneNumber || name);
  return { label: phone || `Client ${u?.id ?? '—'}`, named: false };
}

/**
 * Users joined to their portfolios — the Clients table's row model.
 *
 * The desk's own clients and nobody else: the ids come from the firm's
 * APPROVED client links, and are the query's `ids` argument rather than a
 * filter applied afterwards, so another firm's client is never fetched. With
 * no approved client the query is not sent at all.
 */
export function useClients() {
  const scope = useClientScope();
  const users = useQuery(GET_CLIENTS, {
    variables: { ids: scope.ids.map(Number) },
    /* The individual is read from their own profile, not the user register. */
    skip: scope.loading || scope.ids.length === 0 || scope.self,
    fetchPolicy: 'cache-first',
  });
  const me = useProfile();
  const book = useBook();

  /**
   * Refetching an empty scope would ask for EVERY user.
   *
   * `skip` suppresses the initial fetch but not `refetch()`, which forces
   * network-only with whatever variables the query carries — and `ids: []`
   * reaches the backend as no filter at all (`if ids:` in crud/user.py), so it
   * answers with the whole platform. A desk with no approved client can still
   * reach a refetch (adding a prospect is not firm-gated), so the guard is
   * here rather than in the callers.
   */
  const empty = scope.ids.length === 0;
  const refetch = useCallback(
    () => (empty ? Promise.resolve(null) : users.refetch()),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [empty, users.refetch],
  );

  /* The individual's one row, from their own profile. */
  const selfUser = useMemo(() => (scope.self && me.user && scope.ids.length
    ? { ...me.user, id: scope.ids[0], userType: me.user.userType ?? 'USER', isActive: true }
    : null), [scope.self, scope.ids, me.user]);

  return useMemo(() => {
    /* Filtered again on the way in: `ids` is the server's to honour, and a
       desk must not show a person its firm has no link to even if it does not. */
    const items = scope.self
      ? (selfUser ? [selfUser] : EMPTY)
      : (users.data?.getAllUsers?.items ?? EMPTY).filter((u) => scope.set.has(String(u.id)));
    const byUser = new Map();
    book.book.forEach((p) => {
      if (!p.userId) return;
      const list = byUser.get(String(p.userId)) || [];
      list.push(p);
      byUser.set(String(p.userId), list);
    });

    const rows = items
      .map((u) => {
        const ps = byUser.get(String(u.id)) || EMPTY;
        const aum = sum(ps, (p) => p.marketData?.marketValue);
        const invested = sum(ps, (p) => p.totalInvestedAmount);
        const pnl = sum(ps, (p) => p.returnMetrics?.totalPnl);
        const sip = sum(ps, (p) => p.goalData?.monthlyContribution);
        const goals = ps.filter((p) => p.goalData?.goalId);
        const onTrack = goals.filter((p) => p.goalData?.goalStatus === 'ON_TRACK').length;
        const drift = Math.max(0, ...ps.map((p) => Math.abs(Number(p.risk?.riskVsTarget) || 0)));
        const worstDd = Math.min(0, ...ps.map((p) => Number(p.risk?.maxDrawdown) || 0));
        const primary = [...ps].sort(byDesc((p) => p.marketData?.marketValue))[0];
        /* Not the client scope above — what this client's own portfolios are
           (self / family / external), which the row shows as a label. */
        const pfScope = portfolioScope(ps);

        const named = clientName(u);
        return {
          id: String(u.id),
          name: named.label,
          /* False when the label is a contact standing in for a name — the
             account has none on file. */
          hasName: named.named,
          email: u.email,
          phone: u.phoneNumber,
          kyc: u.kycVerified,
          pan: u.kycPanMasked,
          userType: u.userType,
          isActive: u.isActive,
          since: u.createdAt,
          lastActivity: primary?.latestDataDate || u.updatedAt,
          portfolios: ps,
          primarySymbol: primary?.securitySymbol || null,
          /**
           * What the client holds, counted and described.
           *
           * Was `accounts: ps.length`, which read on screen as logins or bank
           * links. They are portfolios, and almost all of them are one goal's
           * money — so the screens say goals and the label handles the client
           * who also holds something else.
           */
          scope: pfScope,
          scopeLabel: portfolioScopeLabel(pfScope),
          aum,
          invested,
          pnl,
          returnPct: invested ? (pnl / invested) * 100 : null,
          xirr: primary?.returnMetrics?.annualizedReturnSi ?? primary?.returnMetrics?.annualizedReturn1y ?? null,
          sip,
          goals: goals.length,
          goalsOnTrack: onTrack,
          drift,
          worstDrawdown: worstDd,
        };
      })
      .sort(byDesc((r) => r.aum));

    const withMoney = rows.filter((r) => r.aum > 0);
    /* Every client for a `SearchSelect`, grouped so the ones with a portfolio
       to read come first. One list, so every client picker offers the same book. */
    const asOption = (c) => ({ value: c.id, label: c.name });
    const options = [
      { label: 'With holdings', options: withMoney.map(asOption) },
      { label: 'No holdings yet', options: rows.filter((r) => !(r.aum > 0)).map(asOption) },
    ].filter((g) => g.options.length);

    return {
      loading: scope.loading || users.loading || book.loading || (scope.self && me.loading),
      error: scope.error || users.error || book.error,
      /** True when there is no firm yet — the reason for an empty book is not
       *  "no clients" but "no firm", and screens say different things. */
      noFirm: scope.noFirm,
      refetch,
      rows,
      withMoney,
      options,
      total: users.data?.getAllUsers?.total ?? rows.length,
    };
  }, [users.data, users.loading, users.error, refetch, book, scope, me, selfUser]);
}

/**
 * How each KYC state should read, and how alarmed to look about it.
 *
 * The five come from `getKycStatusEnum`. Two more states exist that the enum
 * does not name: a client who has not started onboarding has no record at all,
 * and a caller who may not read one gets an error — neither is "pending", and
 * calling them that is the bug this replaces.
 */
const KYC_STATES = {
  VERIFIED: { label: 'KYC verified', tone: 'green' },
  IN_PROGRESS: { label: 'KYC in progress', tone: 'amber' },
  PENDING: { label: 'KYC pending', tone: 'amber' },
  REJECTED: { label: 'KYC rejected', tone: 'red' },
  EXPIRED: { label: 'KYC expired', tone: 'red' },
};

const KYC_NOT_STARTED = { label: 'KYC not started', tone: 'grey' };
const KYC_UNKNOWN = { label: 'KYC unknown', tone: 'grey' };
/* Not a state of the client's KYC at all — a refusal to show it. The backend
   answers "403: Not authorised to view another user's KYC" to a firm member
   asking about their own approved client (`resolve_target_user_id` only lets
   staff act for someone else), so the desk cannot read it however the client
   stands. Saying "unknown" reads as a gap in the client's onboarding; this
   says whose limitation it is. */
const KYC_REFUSED = { label: 'KYC not shared with the firm', tone: 'grey' };
const KYC_REFUSAL = /not authorised to view another user|approved firm membership required/i;

/**
 * One client's KYC standing.
 *
 * The onboarding record answers when it can be read. When it cannot — the
 * caller is not permitted to read another user's, or the call failed — the
 * user row's `kycVerified` is honoured ONLY when it is true, because a true
 * there is a positive claim while a false is the absence of one. Falling back
 * to a false and printing "pending" is what made every verified client look
 * unverified.
 */
export function useClientKyc(client) {
  const userId = Number(client?.id);
  const { data, loading, error } = useQuery(KYC_ONBOARDING, {
    variables: { userId },
    skip: !Number.isFinite(userId),
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });

  const res = data?.getKycOnboarding ?? null;
  const flagged = Boolean(client?.kyc);
  const refused = KYC_REFUSAL.test(String(error?.message ?? ''));

  return useMemo(() => {
    const status = res?.kycStatus || null;
    const state = status
      ? KYC_STATES[status] ?? { label: `KYC ${String(status).toLowerCase().replace(/_/g, ' ')}`, tone: 'grey' }
      /* `success` with no status is a real answer: onboarding never began. */
      : res?.success
        ? KYC_NOT_STARTED
        : flagged
          ? KYC_STATES.VERIFIED
          : refused
            ? KYC_REFUSED
            : KYC_UNKNOWN;

    return {
      loading,
      /* A refusal is reported as `refused`, not as a broken screen. */
      error: refused ? null : error,
      refused,
      status,
      label: state.label,
      tone: state.tone,
      verified: status === 'VERIFIED' || (!status && flagged),
      /* How far the form got, which moves independently of the status. */
      step: res?.currentStep ?? null,
      externalStatus: res?.externalKycStatus ?? null,
      ucc: res?.ucc ?? null,
      canTransact: String(res?.uccStatus || '').toUpperCase() === 'ACTIVE',
      message: res?.message ?? null,
      /* The stored form, for the edit panel to open on. Null when onboarding
         never began, which the panel reads as "start from blank". */
      accountId: res?.accountId ?? null,
      linkSentAt: res?.kycLinkSentAt ?? null,
      data: res?.data ?? null,
    };
  }, [res, flagged, refused, loading, error]);
}

export function useProspects() {
  const { data, loading, error, refetch } = useQuery(GET_PROSPECTS, {
    variables: { filter: null },
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });
  return {
    loading,
    error,
    refetch,
    rows: data?.listProspects?.items ?? EMPTY,
    total: data?.listProspects?.total ?? 0,
  };
}
